import { useCallback, useRef, useState } from 'react';

import { useStorageToken } from '~lib/auth/use-storage-token';
import { createTemporaryLink, type TemporaryLink } from '~lib/storage/temporary-link';

export type TemporaryLinkState =
  | { status: 'idle' }
  | { status: 'creating' }
  | { status: 'ready'; link: TemporaryLink }
  | { status: 'error'; error: Error };

interface UseTemporaryLinkResult {
  state: TemporaryLinkState;
  create: (blobName: string, ttlMs: number) => Promise<void>;
  reset: () => void;
}

// Wraps createTemporaryLink with token acquisition and a small state machine
// for the dialog. Only the most recent `create` call may publish its result,
// so flipping the validity twice in quick succession can't leave a stale URL
// on screen.
export function useTemporaryLink(): UseTemporaryLinkResult {
  const getToken = useStorageToken();
  const [state, setState] = useState<TemporaryLinkState>({ status: 'idle' });
  const requestId = useRef(0);

  const create = useCallback(
    async (blobName: string, ttlMs: number) => {
      const id = ++requestId.current;
      setState({ status: 'creating' });
      try {
        const token = await getToken();
        const link = await createTemporaryLink(token, blobName, ttlMs);
        if (requestId.current === id) setState({ status: 'ready', link });
      } catch (e) {
        if (requestId.current === id) {
          setState({
            status: 'error',
            error: e instanceof Error ? e : new Error(String(e)),
          });
        }
      }
    },
    [getToken],
  );

  const reset = useCallback(() => {
    requestId.current += 1;
    setState({ status: 'idle' });
  }, []);

  return { state, create, reset };
}
